import { useState, useEffect, useCallback, useRef } from 'react';
import { getConfig } from '../config';

const CONSOLE_APP_CLIENT_ID = '455ea777-60d4-457c-98bf-05b4b5d8c207';
const CONSOLE_PKCE_KEY = 'console_app_pkce';
const CONSOLE_TOKEN_KEY = 'console_app_token';
const CONSOLE_TOKEN_EXPIRES_KEY = 'console_app_token_expires';
// Warn when less than 10 minutes remain before expiry
const TOKEN_WARN_REMAINING_MS = 10 * 60 * 1000;

interface ConsoleAuthState {
  token: string | null;
  expiresAt: number | null;
  isAuthenticating: boolean;
  error: string | null;
}

function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return await crypto.subtle.digest('SHA-256', data);
}

function base64UrlEncode(arrayBuffer: ArrayBuffer): string {
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function toUrlEncoded(obj: Record<string, string>): string {
  return Object.entries(obj)
    .map(([key, value]) => {
      const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      return `${encodeURIComponent(snakeKey)}=${encodeURIComponent(value)}`;
    })
    .join('&');
}

/**
 * Hook that provides a popup-based OAuth PKCE flow to get a token from the
 * PlatformConsoleApp. This token can create organizations and applications
 * that the regular app token cannot.
 */
export function useConsoleAppAuth() {
  const storedExpiry = sessionStorage.getItem(CONSOLE_TOKEN_EXPIRES_KEY);
  const [state, setState] = useState<ConsoleAuthState>({
    token: sessionStorage.getItem(CONSOLE_TOKEN_KEY),
    expiresAt: storedExpiry ? parseInt(storedExpiry, 10) : null,
    isAuthenticating: false,
    error: null,
  });

  const popupRef = useRef<Window | null>(null);

  // Listen for token messages from the popup callback page
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === 'CONSOLE_AUTH_SUCCESS' && event.data?.token) {
        const expiresAt: number | null = event.data.expiresAt ?? null;
        sessionStorage.setItem(CONSOLE_TOKEN_KEY, event.data.token);
        if (expiresAt) sessionStorage.setItem(CONSOLE_TOKEN_EXPIRES_KEY, String(expiresAt));
        setState({ token: event.data.token, expiresAt, isAuthenticating: false, error: null });
        if (popupRef.current) {
          try { popupRef.current.close(); } catch {}
          popupRef.current = null;
        }
      }

      if (event.data?.type === 'CONSOLE_AUTH_ERROR') {
        setState(prev => ({
          ...prev,
          isAuthenticating: false,
          error: event.data.error || 'Authentication failed',
        }));
        if (popupRef.current) {
          try { popupRef.current.close(); } catch {}
          popupRef.current = null;
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Monitor popup close
  useEffect(() => {
    if (!popupRef.current) return;
    const timer = setInterval(() => {
      if (popupRef.current?.closed) {
        clearInterval(timer);
        popupRef.current = null;
        setState(prev => {
          if (prev.isAuthenticating) {
            return { ...prev, isAuthenticating: false, error: 'Popup closed before authentication completed' };
          }
          return prev;
        });
      }
    }, 500);
    return () => clearInterval(timer);
  }, [state.isAuthenticating]);

  const authenticate = useCallback(async () => {
    const config = getConfig();
    const passportBase = config.passportServiceOrigin + '/passportsvc/api';
    const authorizeUrl = `${passportBase}/v2/oauth/authorize`;

    // The popup will redirect back to a static HTML file (no hash router)
    const redirectUri = `${window.location.origin}/console-auth-callback.html`;

    // Generate PKCE codes
    const codeVerifier = generateRandomString(64);
    const hashed = await sha256(codeVerifier);
    const codeChallenge = base64UrlEncode(hashed);

    // Store PKCE verifier for the callback to use (static HTML reads this)
    sessionStorage.setItem(CONSOLE_PKCE_KEY, JSON.stringify({
      codeVerifier,
      redirectUri,
      passportOrigin: config.passportServiceOrigin,
    }));

    const params: Record<string, string> = {
      clientId: CONSOLE_APP_CLIENT_ID,
      responseType: 'code',
      redirectUri,
      scope: 'read write',
      codeChallenge,
      codeChallengeMethod: 'S256',
    };

    const queryString = toUrlEncoded(params);
    const url = `${authorizeUrl}?${queryString}`;

    // Open popup
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2.5;
    const popup = window.open(url, 'Console App Login', `width=${width},height=${height},left=${left},top=${top}`);

    if (!popup) {
      setState(prev => ({ ...prev, error: 'Popup blocked. Please allow popups for this site.' }));
      return;
    }

    popupRef.current = popup;
    setState({ token: state.token, isAuthenticating: true, error: null, expiresAt: state.expiresAt });
  }, [state.token]);

  const clearToken = useCallback(() => {
    sessionStorage.removeItem(CONSOLE_TOKEN_KEY);
    sessionStorage.removeItem(CONSOLE_TOKEN_EXPIRES_KEY);
    setState({ token: null, expiresAt: null, isAuthenticating: false, error: null });
  }, []);

  const remainingMs = state.token && state.expiresAt ? state.expiresAt - Date.now() : null;
  const isTokenExpiring = remainingMs !== null && remainingMs <= TOKEN_WARN_REMAINING_MS;

  return {
    consoleToken: state.token,
    expiresAt: state.expiresAt,
    isTokenExpiring,
    isAuthenticating: state.isAuthenticating,
    error: state.error,
    authenticate,
    clearToken,
    hasToken: !!state.token,
  };
}

